import React from "react";
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    Divider,
    Button,
    Stack,
    Collapse,
    IconButton,
    Chip,
} from "@mui/material";
import { Description, Link, Phone, Message, ExpandMore, ExpandLess, ContentCopy, PhoneAndroid } from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const TemplatePreview = ({
    templateName,
    language,
    category,
    header,
    body,
    footer,
    buttons,
    headerMediaPreviewUrl,
}) => {
    const [expanded, setExpanded] = React.useState(true);

    const headerComponent = header?.type === "TEXT" 
        ? { type: "HEADER", format: "TEXT", text: header.text }
        : header?.type && ["IMAGE", "VIDEO", "DOCUMENT"].includes(header.type)
        ? { type: "HEADER", format: header.type }
        : null;

    const bodyComponent = body?.text ? { type: "BODY", text: body.text } : null;
    const footerComponent = footer?.text ? { type: "FOOTER", text: footer.text } : null;
    const buttonsComponent = buttons && buttons.length > 0 ? { type: "BUTTONS", buttons } : null;

    // Build preview text with parameters
    const replaceParameters = (text, paramArray) => {
        if (!text || !paramArray) return text || "";
        let result = text;
        paramArray.forEach((param, index) => {
            result = result.replace(
                new RegExp(`\\{\\{${index + 1}\\}\\}`, "g"),
                param || `{{${index + 1}}}`
            );
        });
        return result;
    };

    const headerText =
        headerComponent?.format === "TEXT"
            ? replaceParameters(headerComponent.text, header?.examples || [])
            : null;

    const bodyText = bodyComponent
        ? replaceParameters(bodyComponent.text, body?.examples || [])
        : null;

    // WhatsApp background pattern
    const whatsappBgPattern = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAAAnSURBVHic7cEBDQAAAMIg+6c2BL4mAAAAAAAAAAAAAAAAAAD4B7a4AAFj8b8zAAAAAElFTkSuQmCC")`;

    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                ...WB_SX.card,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Collapsible Header */}
            <Box
                sx={{
                    backgroundColor: "#075e54",
                    color: "white",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        WhatsApp Preview
                    </Typography>
                    <Chip
                        label={expanded ? "Expanded" : "Collapsed"}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            backgroundColor: "rgba(255,255,255,0.2)",
                            color: "white",
                        }}
                    />
                </Box>
                <IconButton
                    size="small"
                    sx={{ color: "white" }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(!expanded);
                    }}
                >
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>

                    {/* WhatsApp Header */}
                    <Box
                        sx={{
                            backgroundColor: "#075e54",
                            color: "white",
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: "#25d366",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                            }}
                        >
                            WA
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                WhatsApp Business
                            </Typography>
                        </Box>
                    </Box>

                    {/* WhatsApp Chat Area */}
                    <Box
                        sx={{
                            backgroundColor: "#e5ddd5",
                            backgroundImage: whatsappBgPattern,
                            minHeight: 300,
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {/* Template Message Bubble - Received */}
                        <Card
                            sx={{
                                maxWidth: "75%",
                                mr: "auto",
                                ml: 0,
                                backgroundColor: "#ffffff",
                                borderRadius: "0 7.5px 7.5px 7.5px",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                border: "1px solid #e5e5e5",
                            }}
                        >
                            <CardContent sx={{ p: 1, "&:last-child": { pb: 1.5 } }}>
                                {/* Header (Text) */}
                                {headerComponent?.format === "TEXT" && headerText && (
                                    <>
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                            sx={{ mb: 1, color: "#075e54" }}
                                        >
                                            {headerText}
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                    </>
                                )}

                                {/* Header (Media) */}
                                {headerComponent &&
                                    ["IMAGE", "VIDEO", "DOCUMENT"].includes(
                                        headerComponent.format
                                    ) && (
                                        <Box sx={{ mb: 1 }}>
                                            {headerMediaPreviewUrl && headerMediaPreviewUrl.trim() ? (
                                                <Box>
                                                    {headerComponent.format === "IMAGE" && (
                                                        <Box
                                                            component="img"
                                                            src={headerMediaPreviewUrl}
                                                            alt="Preview"
                                                            sx={{
                                                                width: "100%",
                                                                maxHeight: 200,
                                                                borderRadius: 1,
                                                                display: "block",
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    )}
                                                    {headerComponent.format === "VIDEO" && (
                                                        <Box
                                                            component="video"
                                                            src={headerMediaPreviewUrl}
                                                            controls
                                                            sx={{
                                                                width: "100%",
                                                                maxHeight: 200,
                                                                borderRadius: 1,
                                                                display: "block",
                                                            }}
                                                        />
                                                    )}
                                                    {headerComponent.format === "DOCUMENT" && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                                p: 1.5,
                                                                backgroundColor: "#f5f5f5",
                                                                borderRadius: 1,
                                                            }}
                                                        >
                                                            <Description fontSize="large" color="primary" />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" fontWeight="bold">
                                                                    Document
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Ready to send
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Media will appear here
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                {/* Body */}
                                {bodyText && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            lineHeight: 1.5,
                                            whiteSpace: "pre-wrap",
                                            mb: footerComponent?.text || buttonsComponent?.buttons ? 1 : 0,
                                        }}
                                    >
                                        {bodyText}
                                    </Typography>
                                )}

                                {/* Footer */}
                                {footerComponent?.text && (
                                    <>
                                        {(bodyText || headerComponent) && <Divider sx={{ my: 1 }} />}
                                        <Typography
                                            variant="caption"
                                            sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                                        >
                                            {footerComponent.text}
                                        </Typography>
                                    </>
                                )}

                                {/* Buttons */}
                                {buttonsComponent?.buttons && buttonsComponent.buttons.length > 0 && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Stack spacing={1}>
                                            {buttonsComponent.buttons.map((button, index) => {
                                                const getButtonIcon = () => {
                                                    const buttonType = button.type || "QUICK_REPLY";
                                                    switch (buttonType) {
                                                        case "URL":
                                                            return <Link fontSize="small" />;
                                                        case "PHONE_NUMBER":
                                                            return <Phone fontSize="small" />;
                                                        case "OTP":
                                                            return button.otp_type === "ONE_TAP"
                                                                ? <PhoneAndroid fontSize="small" />
                                                                : <ContentCopy fontSize="small" />;
                                                        case "QUICK_REPLY":
                                                        default:
                                                            return <Message fontSize="small" />;
                                                    }
                                                };

                                                const buttonLabel = button.text
                                                    || (button.otp_type === "ONE_TAP" ? "Autofill" : button.otp_type === "COPY_CODE" || button.type === "OTP" ? "Copy Code" : button.type);

                                                return (
                                                    <Button
                                                        key={index}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        startIcon={getButtonIcon()}
                                                        sx={{
                                                            borderColor: "#25d366",
                                                            color: "#25d366",
                                                            textTransform: "none",
                                                            fontSize: "0.75rem",
                                                            justifyContent: "center",
                                                            "&:hover": {
                                                                borderColor: "#128c7e",
                                                                backgroundColor: "rgba(37, 211, 102, 0.1)",
                                                            },
                                                        }}
                                                    >
                                                        {buttonLabel}
                                                    </Button>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}
                            </CardContent>

                            {/* Message Time */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    px: 1.5,
                                    pb: 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: "0.7rem",
                                    }}
                                >
                                    {new Date().toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </Typography>
                            </Box>
                        </Card>

                        {/* Info Message */}
                        <Card
                            sx={{
                                maxWidth: "80%",
                                ml: "auto",
                                backgroundColor: "#dcf8c6",
                                borderRadius: "7.5px",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                            }}
                        >
                            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                <Typography variant="caption" color="text.secondary">
                                    Preview of your template message
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* WhatsApp Footer */}
                    <Box
                        sx={{
                            backgroundColor: "#f0f0f0",
                            p: 1.5,
                            textAlign: "center",
                            borderTop: "1px solid #e5e5e5",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Template message • No replies
                        </Typography>
                    </Box>

                    {/* Template Info */}
                    {(category || language) && (
                        <Box sx={{ p: 1.5, backgroundColor: "#f9f9f9", display: "flex", gap: 1, flexWrap: "wrap", borderTop: `1px solid ${WB_UI.color.border}` }}>
                            {category && (
                                <Chip
                                    label={category}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        backgroundColor:
                                            category === "AUTHENTICATION"
                                                ? "#e8f5e9"
                                                : category === "MARKETING"
                                                ? "#fff3e0"
                                                : "#e3f2fd",
                                        color:
                                            category === "AUTHENTICATION"
                                                ? "#2e7d32"
                                                : category === "MARKETING"
                                                ? "#ed6c02"
                                                : "#1976d2",
                                    }}
                                />
                            )}
                            {language && (
                                <Chip
                                    label={language}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderColor: WB_UI.color.border, fontWeight: 600 }}
                                />
                            )}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default TemplatePreview;

